import { Box, FormControl, Grid, IconButton, Tooltip } from '@mui/material';
import { StyledOutlinedInput, StyledFormLabel } from 'components/atoms';
import {
  InputFieldProps,
  withGridItemCSS,
  withWrapperCSS,
} from 'components/organisms/forms/form-builder/types';
import { InfoSimpleModal } from 'components/organisms/modals';
import { noop } from 'lodash';
import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { BsFillInfoCircleFill } from 'react-icons/bs';
import { FaCheck } from 'react-icons/fa6';
import { HiOutlineMinusSm } from 'react-icons/hi';
import { IoMdAdd } from 'react-icons/io';

const infoModalText =
  'Input the initial step of your exercise and press the Enter key to reveal an input field for your subsequent step. Alternatively, utilize the plus sign to append additional input fields. This facilitates the seamless progression of steps within the exercise.';

export const MultipleInputField: FC<
  InputFieldProps<string[]> & withGridItemCSS & withWrapperCSS
> = ({
  disabled = false,
  error,
  fieldKey,
  gridItemCss,
  initValue = [],
  label = '',
  onChange = noop,
  labelCss,
  required = false,
  type = 'text',
  wrapperCss,
  xs = 12,
}) => {
  const [openInfo, setOpenInfo] = useState(false);
  const [hasError, setHasError] = useState(error ? error : false);
  const [valueList, setValueList] = useState(initValue.length > 0 ? initValue : ['']);
  const inputRefs = useRef<HTMLInputElement[]>([]);

  const onFieldChange = () => {
    if (required && valueList[0] === '') setHasError(true);
    else setHasError(false);
    onChange(valueList, fieldKey);
  };

  useEffect(() => {
    onFieldChange();
  }, [valueList]);

  useEffect(() => {
    setValueList(initValue.length > 0 ? initValue : ['']);
  }, [initValue]);

  useEffect(() => {
    if (valueList.length > 1 && valueList.length !== initValue.length) {
      inputRefs.current[valueList.length - 1].focus();
    }
  }, [valueList.length]);

  const handleInputChange = (
    index: number,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newInputs = [...valueList];
    newInputs[index] = event.target.value;
    setValueList(newInputs);
  };

  const focusLastInput = () => {
    const lastInputElement = inputRefs.current[inputRefs.current.length - 1];
    if (lastInputElement) {
      lastInputElement.focus();
    }
  };

  const handleAddInput = () => {
    setValueList((current) => [...current, '']);
    setTimeout(focusLastInput, 0);
  };

  const keyInputListener = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    value: string,
    index: number,
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (index < valueList.length - 1) handleRemoveInput(index);
      else if (value.length > 0) {
        handleAddInput();
      }
    }
  };

  const handleRemoveInput = (index: number) => {
    setValueList((current) => {
      const newList = current.filter((_, i) => i !== index);
      return newList;
    });
  };

  return (
    <Grid item xs={xs} style={gridItemCss}>
      {valueList.map((val, index) => (
        <FormControl
          key={index}
          error={!!hasError}
          required={required}
          style={{ width: '100%', ...wrapperCss }}
        >
          {index === 0 ? (
            <StyledFormLabel error={!!hasError} style={labelCss}>
              {label} {required ? '*' : null}
              <IconButton onClick={() => setOpenInfo(true)} size="small">
                <BsFillInfoCircleFill />
              </IconButton>
            </StyledFormLabel>
          ) : null}
          <Box key={index} display="flex" alignItems="center" mb={1}>
            <StyledOutlinedInput
              id={fieldKey + '-' + index}
              name={fieldKey + '-' + index}
              inputRef={(el: HTMLInputElement) => {
                inputRefs.current[index] = el;
              }}
              disabled={disabled}
              value={val}
              onChange={(event) => handleInputChange(index, event)}
              onKeyDown={(event) => keyInputListener(event, val, index)}
              placeholder={''}
              required={required}
              type={type}
              fullWidth
              inputProps={{
                'aria-label': `${label} ${index + 1}`,
                'data-testid': `${fieldKey}-row-${index}`,
              }}
              endAdornment={
                index === valueList.length - 1 ? (
                  <Tooltip title="Add Another Item" placement="top">
                    <IconButton
                      onClick={handleAddInput}
                      disabled={val === ''}
                      type={'button'}
                    >
                      <IoMdAdd />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="Remove Item" placement="top">
                    <IconButton onClick={() => handleRemoveInput(index)} type={'button'}>
                      <HiOutlineMinusSm />
                    </IconButton>
                  </Tooltip>
                )
              }
            />
          </Box>
        </FormControl>
      ))}
      <InfoSimpleModal
        icon={FaCheck}
        open={openInfo}
        oneButton={true}
        onClose={() => setOpenInfo(false)}
        onSubmit={() => setOpenInfo(false)}
        text={[infoModalText]}
        title={label}
      />
    </Grid>
  );
};
